import {memo, useCallback} from 'react';

import {useField} from 'react-final-form';
import {useSelector} from 'react-redux';
import styled from 'styled-components';

import AgeRestrictionBadge from '~/shared/components/AgeRestrictionBadge';
import BaseAmountCounter from '~/shared/components/AmountCounter';
import DishPricePerWeight from '~/shared/components/DishPricePerWeight';
import {useIsMaxTablet, useIsMinTablet} from '~/shared/hooks/deviceInfo';
import {getLocalizationService} from '~/shared/services/localisationService';
import {Dish, RestaurantBusinessTypeName} from '~/shared/store/models';
import {selectCurrentCoupon, selectCurrentRestaurant} from '~/shared/store/selectors';
import {flexCenterVertically, FlexColumn, FlexSpace, FlexStart} from '~/shared/theme/FlexLayout';
import {body13Normal, body14Bold, body16Bold, body18Normal, body20Bold} from '~/shared/theme/typography';
import {media, flipOnLTR, rotateOnLTR} from '~/shared/theme/utils';
import {getDishPriceWithDiscount} from '~/shared/utils/priceDiscountCalculator';
import ArrowLeft from '~/assets/images/svgIconsAsText/back-arrow.svg?react';
import Button from '~/shared/components/Button';
import DefaultImage from '~/assets/images/icons/groceries-default-image.svg?react';

import useTotalPrice from '../../../hooks/useTotalPrice';
import BaseDishPrice from '../DishPrice';

import AssignedUser from './AssignedUser';
import AlcoholPercentageWarning from './AlcoholPercentageWarning';
import HighStatsAlerts from './HighStatsAlerts';

const Root = styled.div`
  ${media.minTablet`
		padding: 20px 20px 0 20px;
	`}
`;

const Image = styled.div<{backgroundImage?: string}>`
  display: block;
  width: 100%;
  height: 180px;
  background-image: url(${({backgroundImage}) => backgroundImage});
  background-size: cover;
  background-repeat: no-repeat;
  background-position: center;
  background-color: ${({theme}) => theme.colors.darkBackground};
  ${media.minTablet`
    width: 244px;
    height: 207px;
  `};
`;

const ContentContainer = styled(FlexStart)`
  align-items: stretch;
`;

const DishInfo = styled(FlexColumn)`
  min-height: 150px;
  flex: 1;
  padding: 20px 20px 0 20px;
  gap: 8px;
  ${media.minTablet`
    padding: 0;
    ${flipOnLTR`
      padding-right: 20px;
    `}
	`}
`;

const DishName = styled.h1`
  ${body20Bold};
  color: ${({theme}) => theme.menu.dishModal.textColor};
  ${flipOnLTR`
		text-align: right;
	`}
  ${media.minTablet`
    line-height: 1em;
  `}
`;

const DishDescription = styled.div`
  ${body14Bold};
  width: 90%;
  line-height: 16px;
  color: ${({theme}) => theme.menu.dishModal.textColor};
  ${flipOnLTR`
    text-align: right;
  `}
  ${media.minTablet`
    margin-top: 0;
    font-weight: normal;
  `};
`;

const ActionsContainer = styled(FlexSpace)<{withBorderBottom?: boolean; withBorderTop?: boolean; withPadding?: boolean; withExtraMargin?: boolean}>`
  flex: 1;
  align-items: flex-end;
  margin-top: ${({withExtraMargin}) => (withExtraMargin ? '24px' : '8px;')};
  padding: ${({withPadding}) => (withPadding ? '15px 0' : '0')};
  border-top: ${({theme, withBorderTop}) => withBorderTop && `1px solid ${theme.colors.darkBackground}`};
  border-bottom: ${({theme, withBorderBottom}) => withBorderBottom && `1px solid ${theme.colors.darkBackground}`};
  ${media.minTablet`
    padding: 0;
    margin-bottom: 8px;  
	`}
`;

const Line = styled.div`
  width: 100%;
  height: 1px;
  border-radius: 2px;
  background-color: ${({theme}) => theme.lineColor};
  margin: 10px 0;
`;

const StyledArrowLeft = styled(ArrowLeft)`
  ${rotateOnLTR}
  scale: 1.15;
`;

const AdditionalInfoContainer = styled(ActionsContainer)`
  margin-top: 8px;
  display: flex;
`;

const StyledButton = styled(Button)`
  width: 100%;
  height: 60px;
  ${flexCenterVertically}
`;

const Text = styled.span`
  display: flex;
  flex: 1;
  ${body16Bold}
  color: ${({theme}) => theme.colors.secondary};
`;

const AmountCounter = styled(BaseAmountCounter)``;

const DishPrice = styled(BaseDishPrice)`
  color: ${({theme}) => theme.colors.secondary};
  ${body18Normal};
  margin: 0;
  ${flipOnLTR`
    margin-right: 15px;
  `}

  ${media.minTablet`
    ${body20Bold};
    
    ${flipOnLTR`
      margin-right: 30px;
      margin-left: 45px;
    `}
  `}
`;

const UnderImageText = styled.p`
  ${body13Normal};
  margin-top: 5px;
  ${flipOnLTR`
    text-align: right;
  `}
`;

const StyledAgeRestrictionBadge = styled(AgeRestrictionBadge)`
  margin: 0;
  position: absolute;
  z-index: 1;
  top: 16px;
  ${flipOnLTR`
    right: 16px;
  `}

  ${media.minLargeMobile`
    top: 12px;
    ${flipOnLTR`
      right: 12px;
    `}
  `}
`;

const DishNameContainer = styled.div`
  display: flex;
  ${flipOnLTR`
    margin-left: 40px;
  `}
`;

const ImageContainer = styled.div<{defaultImage?: boolean}>`
  display: flex;
  flex-direction: column;
`;

const DefaultImageWrapper = styled(Image)`
  display: flex;
  justify-content: center;
  align-items: center;
  background-color: ${({theme}) => theme.colors.actionSecondary};
  border-radius: 8px;
`;

const StyledDefaultImage = styled(DefaultImage).attrs({viewBox: '0 0 48 48'})`
  width: 74px;
  height: 74px;
  ${media.minLargeMobile`
    width: 101px;
    height: 101px;
  `}
  g path {
    fill: ${({theme}) => theme.colors.borderStrong};
  }
`;

type DishHeaderProps = {
  isEditMode: boolean;
  hasAdditionalInfo?: boolean;
  image?: Dish['dishImageUrl'];
  name: Dish['dishName'];
  description: Dish['dishDescription'];
  ageRestricted: Dish['ageRestricted'];
  pricePerMeasurementUnit: Dish['pricePerMeasurementUnit'];
  measurementUnitScale: Dish['measurementUnitScale'];
  measurementUnit: Dish['measurementUnit'];
  onAdditionalInfoClick?: () => void;
  showHighSugarAlert: Dish['hasHighSugar'];
  showHighSaturatedFatAlert: Dish['hasHighSaturatedFat'];
  showHighSodiumAlert: Dish['hasHighSodium'];
  alcoholPercentage: Dish['alcoholPercentage'];
};

const DishHeader = ({
  name,
  description,
  image,
  isEditMode,
  ageRestricted,
  pricePerMeasurementUnit,
  measurementUnitScale,
  measurementUnit,
  onAdditionalInfoClick,
  showHighSugarAlert,
  showHighSaturatedFatAlert,
  showHighSodiumAlert,
  alcoholPercentage,
  hasAdditionalInfo,
}: DishHeaderProps) => {
  const isMinTablet = useIsMinTablet();
  const {t} = getLocalizationService();
  const isMaxTablet = useIsMaxTablet();
  const currentRestaurant = useSelector(selectCurrentRestaurant);
  const coupon = useSelector(selectCurrentCoupon);

  const totalPrice = useTotalPrice();
  const {priceAfterDiscount: pricePerMeasurementUnitAfterDiscount} = getDishPriceWithDiscount(pricePerMeasurementUnit, coupon) || {};
  const isVoucherCard = currentRestaurant?.businessType === RestaurantBusinessTypeName.VoucherCard;
  const isIncreaseQuantityDisbled = !!currentRestaurant?.isVoucherEnabled || isVoucherCard;

  const {
    input: {value: quantity, onChange},
  } = useField('quantity');

  const increaseQuantity = useCallback(() => {
    if (!isIncreaseQuantityDisbled) {
      onChange(quantity + 1);
    }
  }, [isIncreaseQuantityDisbled, onChange, quantity]);

  const decreaseQuantity = useCallback(() => {
    if (quantity > 1) {
      onChange(quantity - 1);
    }
  }, [onChange, quantity]);

  if (isMaxTablet) {
    // mobile
    return (
      <Root>
        {image ? (
          <Image data-test-id="dishImageContainer" backgroundImage={image} >
            {ageRestricted && <StyledAgeRestrictionBadge/>}
          </Image>
        ) : (
          <DefaultImageWrapper data-test-id="dishImageContainer">
            <StyledDefaultImage/>
            {ageRestricted && <StyledAgeRestrictionBadge/>}
          </DefaultImageWrapper>
        )}
        <DishInfo>
          <FlexSpace>
            <DishNameContainer>
              <DishName data-test-id="dishNameLabel" id="modal-title">
                {name}
              </DishName>
            </DishNameContainer>
            {isEditMode && <DishPrice price={totalPrice} />}
          </FlexSpace>

          {description && (
            <DishDescription id="modal-content">{description}</DishDescription>
          )}
          <AlcoholPercentageWarning alcoholPercentage={alcoholPercentage} />
          <DishPricePerWeight
            pricePerMeasurementUnit={pricePerMeasurementUnitAfterDiscount || pricePerMeasurementUnit}
            measurementUnit={measurementUnit}
            measurementUnitScale={measurementUnitScale}
          />
          <HighStatsAlerts showHighSugarAlert={showHighSugarAlert} showHighSaturatedFatAlert={showHighSaturatedFatAlert} showHighSodiumAlert={showHighSodiumAlert} />
          {onAdditionalInfoClick && (
            <AdditionalInfoContainer withBorderTop withBorderBottom>
              <StyledButton onClick={onAdditionalInfoClick}>
                <Text>{t('additional_info')}</Text>
                <StyledArrowLeft />
              </StyledButton>
            </AdditionalInfoContainer>
          )}
          <ActionsContainer withBorderTop={!hasAdditionalInfo} withPadding={!hasAdditionalInfo} withExtraMargin={!hasAdditionalInfo && !description}>
            <AssignedUser />

            <AmountCounter
              {...{
                quantity,
                increaseQuantity,
                decreaseQuantity,
                isIncreaseQuantityDisbled,
                itemName: name,
              }}
              size="big"
              disableTabIndex
            />
          </ActionsContainer>
        </DishInfo>
      </Root>
    );
  }

  return (
    <Root>
      <ContentContainer>
        {image ? (
          <ImageContainer>
            <Image data-test-id="dishImageContainer" backgroundImage={image} >
              {ageRestricted && <StyledAgeRestrictionBadge/>}
            </Image>
            <UnderImageText>*{t('the_image_is_for_illustration_purposes_only')}</UnderImageText>
          </ImageContainer>
        ) : (
          <ImageContainer defaultImage >
            <DefaultImageWrapper data-test-id="dishImageContainer">
              <StyledDefaultImage/>
              {ageRestricted && <StyledAgeRestrictionBadge/>}
            </DefaultImageWrapper>
          </ImageContainer>
        )}
        <DishInfo>
          <FlexSpace>
            <DishNameContainer>
              <DishName data-test-id="dishNameLabel" id="modal-title">
                {name}
              </DishName>
            </DishNameContainer>
            {!isMinTablet && <DishPrice price={totalPrice} />}
          </FlexSpace>

          {description && (
            <DishDescription data-test-id="dishDescriptionLabel" id="modal-content">
              {description}
            </DishDescription>
          )}
          <AlcoholPercentageWarning alcoholPercentage={alcoholPercentage} />
          <DishPricePerWeight
            pricePerMeasurementUnit={pricePerMeasurementUnitAfterDiscount || pricePerMeasurementUnit}
            measurementUnit={measurementUnit}
            measurementUnitScale={measurementUnitScale}
          />

          <HighStatsAlerts showHighSugarAlert={showHighSugarAlert} showHighSaturatedFatAlert={showHighSaturatedFatAlert} showHighSodiumAlert={showHighSodiumAlert} />

          <ActionsContainer>
            <AssignedUser />

            <AmountCounter
              {...{
                quantity,
                increaseQuantity,
                decreaseQuantity,
                isIncreaseQuantityDisbled,
                itemName: name,
              }}
              size="big"
              disableTabIndex
            />
          </ActionsContainer>
        </DishInfo>
      </ContentContainer>
      {onAdditionalInfoClick ? (
        <AdditionalInfoContainer withBorderBottom withBorderTop>
          <StyledButton onClick={onAdditionalInfoClick}>
            <Text>{t('additional_info')}</Text>
            <StyledArrowLeft />
          </StyledButton>
        </AdditionalInfoContainer>
      ) : (<Line />)}
    </Root>
  );
};

export default memo(DishHeader);
